import type { EventTracker } from '@core/events/event-manager'
import type { PushSDKLifecycleCallbacks } from './callbacks/push-sdk-lifecycle-callbacks'
import type { PermissionLifecycleCallbacks } from './callbacks/permission-lifecycle-callbacks'
import type { AppMessageLifecycleCallbacks } from './callbacks/app-message-lifecycle-callbacks'
import {
    normalizeConsentState,
    PushSdkConfig,
    pushSdkConfigContainsUnknownKeys,
    type PushSdkConsentState,
} from './types/push-sdk-config'
import {
    Callback,
    PushSdkContext,
    PushSdkEvent,
    PushSdkEventCallback,
    PushSdkPromptEventPayload,
} from './types/push-sdk-legacy'
import type { UserStore } from './storage/user-storage-manager'
import type { SettingsStore } from './storage/settings-storage-manager.ts'
import type { RemoteResourceProvider } from './providers/remote-resource-provider.ts'
import type { PromptLifecycleCallbacks } from './callbacks/prompt-lifecycle-callbacks.ts'
import type { SessionManager } from './session/session-manager.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger'
import { getRandomString } from '@core/utils/random'
import { validateSingleInstance } from './utils/validate-single-instance'
import { DomainSettingsProvider } from './providers/domain-settings-provider'
import {
    type DomainConfig,
    isEventOnlyMode,
    isForceDebugLogsFlagEnabled,
    matchesDomainEntry,
} from '@core/types/domain-config.ts'
import { SubscriberStatus, subscriberStatusToSentenceString } from './enums/subscriber-status'
import { OptOutSource } from './enums/opt-out-source'
import { Globals } from './globals'
import { TrackedEventUtils } from '@core/events/tracked-event'
import { getUuidV7 } from '@core/utils/uuid.ts'
import { LogLevel } from '@core/logging/log-level.ts'
import { NotificationPermissionService, ProviderPermissions } from './permissions/notification-permission-service.ts'
import { PushWorkerService, type WebWorkerService } from './permissions/notifications/push-worker-service.ts'
import { ensureError } from '@core/utils/ensure-error.ts'
import { PageViewProcessor } from './processors/page-view-processor.ts'
import { ScrollDepthObserver } from './processors/scroll-depth-observer.ts'
import { promptGroupsUseScrollDepthTrigger, type PromptGroup } from './types/prompts.ts'
import { PermissionService } from './permissions/permission-service.ts'
import { PromptsController } from './prompts/prompt-controller.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'
import { PromptFailedToShowReason } from './enums/prompt-failed-to-show-reason.ts'
import { PermissionResponse } from './enums/permission-response.ts'
import { Env } from './env.ts'
import { DebugController } from './debug/debug-controller.ts'
import { UserDebugDetails } from './types/user-debug-details.ts'
import { DebugEventReason } from './enums/debug-event-reason.ts'
import { CookieDataStore } from './storage/stores/cookie-data-store.ts'
import { TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { LegacyContextProxyFactory } from './utils/legacy-context-proxy-factory.ts'
import { HeartbeatManager } from './session/heartbeat-manager.ts'
import { SessionStore } from './storage/session-storage-manager.ts'

/** Renders any value for a log line, including those `JSON.stringify` rejects, such as a BigInt. */
function describeValueForLog(value: unknown): string {
    try {
        return JSON.stringify(value) ?? String(value)
    } catch {
        return String(value)
    }
}

/** @internal */
export type PushSdkInternals = {
    readonly userStore: UserStore
    readonly notificationPermissionService: PermissionService<ProviderPermissions> | null
    readonly promptsController: PromptsController | null
    readonly eventManager: EventTracker
    readonly domainConfigProvider: RemoteResourceProvider<DomainConfig>
    readonly debugController: DebugController | null
    readonly isLoaded: boolean
    readonly isEnabled: boolean
    readonly isExited: boolean
    readonly loadConfig: PushSdkConfig
    readonly _legacyContext: PushSdkContext
    _executeWhenConfigured<T>(callback: Callback<T>): Promise<T | null>
    _executeWhenConfiguredIfEnabled<T>(callback: Callback<T>): Promise<T | null>
    _registerPushSdkLifecycleCallbacks(callbacks: PushSDKLifecycleCallbacks): void
    _registerPermissionLifecycleCallbacks(callbacks: PermissionLifecycleCallbacks): void
    _registerAppMessageLifecycleCallbacks(callbacks: AppMessageLifecycleCallbacks): void
    _registerOnLoadedCallback(callback: () => any): void
    _setConfiguration(config: PushSdkConfig): void
    _toggleDebugPanel(): Promise<void>
    _handleUserDeletionRequest(): Promise<void>
    _handleOptOutRequest(): Promise<void>
    _handleOptInRequest(): Promise<void>
    _handleConsentUpdate(state: unknown): Promise<void>
    _registerLegacyEventCallback(event: PushSdkEvent, callback: PushSdkEventCallback): void
}

type LegacyEventCallbackMap = Record<PushSdkEvent, Record<string, PushSdkEventCallback>>

/** @internal */
export class PushSdkInternalImpl
    implements PushSdkInternals, PushSDKLifecycleCallbacks, PermissionLifecycleCallbacks, PromptLifecycleCallbacks
{
    /**
     * Global Instance ID
     * @see getGlobalPushSdk
     */
    public readonly iid = getRandomString(5)

    /**
     * @lateinit
     * @see _setConfiguration
     */
    public domainConfigProvider!: RemoteResourceProvider<DomainConfig>

    /**
     * Owns the single fetch and single cache entry behind both {@link domainConfigProvider}
     * and the prompts provider handed to {@link PromptsController}.
     *
     * @see _setConfiguration
     */
    protected domainSettingsProvider!: DomainSettingsProvider

    public notificationPermissionService: PermissionService<ProviderPermissions> | null = null
    public promptsController: PromptsController | null = null

    /**
     * Built once domain config is known, before the halt check, which needs it to find and remove
     * a subscription. Event-only hosts have one too: they never subscribe, but can still hold a
     * registration.
     */
    protected pushWorkerService: WebWorkerService | null = null

    private readonly _logger: Logger

    private isLoading: boolean
    private _isLoaded: boolean
    private _isExited: boolean
    private _isEnabled: boolean

    private _domainKey!: string
    private _loadConfig!: PushSdkConfig
    protected pageViewProcessor?: PageViewProcessor
    protected scrollDepthObserver?: ScrollDepthObserver

    private _pushSdkLifecycleCallbacks?: PushSDKLifecycleCallbacks
    private _permissionLifecycleCallbacks?: PermissionLifecycleCallbacks
    // @ts-ignore - unused
    private _appMessageLifecycleCallbacks?: AppMessageLifecycleCallbacks
    private promptLifecycleCallbacks?: PromptLifecycleCallbacks

    private _onLoadedCallbacks?: Callback[]
    private _postConfigActionsQueue?: Callback[]
    /** Consent reported through the `consent` command before `load`; it wins over the load key. */
    private _consentStateOverride: PushSdkConsentState | undefined
    /**
     * The consent the load started with, or was released with from a hold: `undefined` when the site
     * reported none. Later runtime changes do not update it.
     */
    private _effectiveConsentState: PushSdkConsentState | undefined
    /** Set while the load waits for the site to report whether consent is granted. */
    private _isLoadHeldForConsent = false
    /**
     * Set the moment the site reports consent denied on a page whose load has passed the consent
     * check, so that startup still running stops short of finishing as a normal load.
     */
    private _isConsentDeniedOnThisPage = false
    /**
     * The tail of the halt marker changes made after the load, which run one at a time. See
     * {@link _runHaltMarkerChangeInTurn}.
     */
    private _haltMarkerChangesInTurn: Promise<unknown> = Promise.resolve()
    private _isImpressionSentForThisPage = false
    private readonly legacyEventCallbacks: LegacyEventCallbackMap
    private _heartbeatManager?: HeartbeatManager

    public constructor(
        public readonly eventManager: EventTracker,
        public readonly settingsStore: SettingsStore,
        public readonly userStore: UserStore,
        public readonly sessionManager: SessionManager,
        public readonly debugController: DebugController,
        public readonly sessionStore: SessionStore,
        logger?: Logger,
    ) {
        this.settingsStore = settingsStore
        this.userStore = userStore
        this._logger = logger ?? TaggedLogger._getInstance()

        this.isLoading = false
        this._isLoaded = false
        this._isExited = false
        this._isEnabled = true

        this.legacyEventCallbacks = {} as LegacyEventCallbackMap

        // Establish the per-tab session_id BEFORE the SDK_IMPRESSION fires so the anchor event of the
        // funnel carries the join key. Runs for all users/domains (never gated); _ensureLateInitValues
        // is the backfill safety net for any event built before the static settles.
        this._establishSessionId().finally(() => this._sendImpressionEvent())
    }

    /**
     * Loads the per-tab `session_id` from sessionStorage or mints a fresh uuidv7, persists it, and
     * publishes it to `TrackedEventUtils._sessionId` so every subsequent event is stamped with it.
     * `HeartbeatManager` owns rotation thereafter.
     */
    protected async _establishSessionId(): Promise<void> {
        try {
            let sessionId = await this.sessionStore._getSessionId()
            if (!sessionId) {
                sessionId = getUuidV7()
                await this.sessionStore._setSessionId(sessionId)
            }
            TrackedEventUtils._sessionId = sessionId
        } catch (err) {
            this._logger.error(ensureError(err))
        }
    }

    protected get _currentPageSearchParams(): URLSearchParams {
        return new URLSearchParams(Env._location.search)
    }

    public async _executeWhenConfigured<T>(callback: Callback<T>): Promise<T | null> {
        if (!this._isLoaded) {
            this._postConfigActionsQueue ??= []
            this._postConfigActionsQueue.push(callback)
            return null as T
        }

        return Promise.resolve(callback())
            .catch((err) => {
                const error = ensureError(err)
                this._logger.error(error)
                this.eventManager._track(error)
            })
            .then((res) => res ?? null)
    }

    public async _executeWhenConfiguredIfEnabled<T>(callback: Callback<T>): Promise<T | null> {
        return this._executeWhenConfigured(() => {
            if (this._isEnabled) {
                return callback()
            }
            return null
        })
    }

    public get loadConfig(): PushSdkConfig {
        return this._loadConfig
    }

    public get isLoaded(): boolean {
        return this._isLoaded
    }

    public get isExited(): boolean {
        return this._isExited
    }

    public get isEnabled(): boolean {
        return this._isEnabled
    }

    public get _legacyContext(): PushSdkContext {
        const proxyFactory = new LegacyContextProxyFactory(this.debugController, this._logger)

        const domain =
            Globals._injectedDomainConfig ?? this.domainConfigProvider?._getCachedSync() ?? ({} as DomainConfig)

        const puuid = this.userStore._getAnonymousIdSync()
        const user = {
            puuid,
            externalId: this.userStore._getExternalIdSync(),
            subscriberState: 'none', // legacy value,
            isSubscribed: false,

            getId: () => puuid,
        }

        try {
            const subscriberState = this.userStore._getSubscriberStatusSync()
            user.isSubscribed = subscriberState === SubscriberStatus.SUBSCRIBED
            if (subscriberState !== SubscriberStatus.NOT_DETERMINED) {
                user.subscriberState = subscriberState
            }
        } catch {}

        return {
            domainKey: this.loadConfig.domainKey,
            env: proxyFactory.get('env'),
            app: proxyFactory.get('app'),
            domain: proxyFactory.get('domain', { ...domain }),
            user: proxyFactory.get('user', user),
        } as any
    }

    protected get _currentLocation(): Location {
        return Env._location
    }

    public _registerPushSdkLifecycleCallbacks(callbacks: PushSDKLifecycleCallbacks) {
        this._pushSdkLifecycleCallbacks = callbacks
    }

    public _registerPermissionLifecycleCallbacks(callbacks: PermissionLifecycleCallbacks) {
        this._permissionLifecycleCallbacks = callbacks
    }

    public _registerAppMessageLifecycleCallbacks(callbacks: AppMessageLifecycleCallbacks) {
        this._appMessageLifecycleCallbacks = callbacks
    }

    public _registerPromptLifecycleCallbacks(callbacks: PromptLifecycleCallbacks) {
        this.promptLifecycleCallbacks = callbacks
    }

    public _registerLegacyEventCallback(event: PushSdkEvent, callback: PushSdkEventCallback, once?: boolean): void {
        this.legacyEventCallbacks[event] ??= {}

        const callbackId = getRandomString(8)
        this.legacyEventCallbacks[event][callbackId] = !once
            ? callback
            : (...args: any[]) => {
                  delete this.legacyEventCallbacks[event][callbackId]
                  callback(...args)
              }
    }

    public _registerOnLoadedCallback(callback: () => any) {
        this._onLoadedCallbacks ??= []
        this._onLoadedCallbacks.push(callback)
    }

    /**
     * Public SDK entrypoint
     */
    public async _setConfiguration(config: PushSdkConfig) {
        const result = validateSingleInstance(this)
        if (result.isErr()) {
            switch (result.getErr().code) {
                case PNExceptionCode.DUPLICATE_SDK_DETECTED: {
                    // The SDK should only exit when a duplicate instance is detected.
                    this._logger.warn('Unable to initialize PushSDK')
                    this._handleExit(result.getErr())
                    break
                }

                case PNExceptionCode.SDK_ALREADY_LOADED: {
                    this._logger.warn('SDK already loaded')
                    await this._applyConsentFromRepeatedLoad(config)
                    break
                }
            }

            return
        }

        if (this._isLoadHeldForConsent) {
            // Loading again is not how a held load is meant to be released, so the integration is
            // told which call it needs, but a consent the repeated load carries still applies.
            this._logger.warn(
                "Initialization is waiting for consent; report it with pushly('consent', state) instead of loading again.",
            )
            await this._applyConsentFromRepeatedLoad(config)
            return
        }

        if (this.isLoading) {
            this._logger.warn('Initialization already started')
            await this._applyConsentFromRepeatedLoad(config)
            return
        }

        // `domainKey` is typed as required but arrives from a caller the type system does not
        // reach - an inline snippet or a queued async command - so an absent value shows up
        // here as `undefined` and used to be interpolated straight into the settings URL.
        // Every such client then requested a document that cannot exist, on every page load,
        // for the lifetime of the integration. Refusing to start is both correct and the only
        // thing that stops the requests.
        if (typeof config?.domainKey !== 'string' || !config.domainKey.trim()) {
            this._logger.error('Unable to initialize PushSDK: a domainKey is required')
            this.isLoading = false
            await this._handleExit(new PNException(PNExceptionCode.DOMAIN_CONFIGURATION_LOAD_FAILED))
            return
        }

        this.isLoading = true

        CookieDataStore._setDomainKey(config.domainKey)

        this._loadConfig = config
        TrackedEventUtils._loadConfig = config
        this._domainKey = config.domainKey

        // Decided before anything starts or is sent, and before the first await, so that a consent
        // change queued after `load` in the same command array finds the load already held or started.
        this._effectiveConsentState =
            this._consentStateOverride ?? this._normalizeConsentStateOrWarn(config.consent, 'load')

        if (this._effectiveConsentState === 'pending') {
            this._holdLoadUntilConsentIsKnown()
            return
        }

        if (this._effectiveConsentState === 'denied') {
            await this._stopLoadAtConsentDenied()
            return
        }

        await this._startLoadAfterConsentCheck(config)
    }

    /**
     * Everything a load does once consent allows it: the session clock, storage housekeeping,
     * configuration checks and the domain settings. A load held for consent starts from here when
     * consent is granted.
     */
    protected async _startLoadAfterConsentCheck(config: PushSdkConfig): Promise<void> {
        // Start session clock as soon as possible after client initialization
        this.sessionManager._start()

        // Not awaited: nothing depends on it, and it only reclaims space in the blob the session
        // clock above re-serialises on every tick.
        this.sessionStore
            ._purgeRetiredKeys()
            .catch((err) => this._logger.verbose(`Unable to purge retired session keys: ${ensureError(err).message}`))

        if (pushSdkConfigContainsUnknownKeys(config)) {
            this.debugController._debugUnknownConfigurationKeys(config)
        }

        this.domainSettingsProvider = new DomainSettingsProvider(config.domainKey, this.settingsStore)
        this.domainConfigProvider = this.domainSettingsProvider._configProvider

        try {
            await this._load()
        } catch (err) {
            const error = ensureError(err)
            this._logger.error(error)
            this.eventManager._track(error)
        }
    }

    /**
     * A value the SDK cannot act on is treated as absent, so the load runs as it did before consent
     * was reported at all. The warning is what tells the integration its value was ignored. An
     * absent value is warned about only where reporting consent is the whole point of the call, as
     * with the `consent` command; a load may leave the setting out.
     */
    protected _normalizeConsentStateOrWarn(
        value: unknown,
        origin: string,
        warnWhenUndefined = false,
    ): PushSdkConsentState | undefined {
        const state = normalizeConsentState(value)
        if (state === undefined && (value !== undefined || warnWhenUndefined)) {
            this._logger.warn(
                `Ignoring consent from ${origin}: expected 'granted', 'denied' or 'pending', got ${describeValueForLog(value)}`,
            )
        }
        return state
    }

    /**
     * Waits for the site to report consent. Nothing has started, so nothing needs stopping, and the
     * subscription is left alone: a visitor whose consent platform has not answered yet has not
     * withdrawn anything. Commands stay queued until the load runs or stops. The page halt also keeps
     * the event queue, which starts when the bundle boots, from sending while consent is unknown.
     */
    protected _holdLoadUntilConsentIsKnown(): void {
        this._logger.info('Waiting for consent before starting the SDK.')
        this.userStore._haltTrackingForThisPage()
        this._isLoadHeldForConsent = true
    }

    /**
     * Stops a load whose site reports consent denied, before anything starts or is sent. A subscribed
     * browser is withdrawn as an opt-out that denied consent caused, so consent granted later undoes
     * it. A browser with nothing to withdraw is halted for this page only: the site reports consent on
     * every page, so nothing needs storing.
     *
     * The domain config is not fetched. A stored one of any age locates the subscription and names
     * the domain on the opt-out; a browser with none has never loaded the SDK here, so it holds no
     * subscription. The one exception is a deletion the URL requests on a browser with no stored
     * config: that is the visitor's own explicit request, and it cannot be acted on without the
     * domain, so the config is fetched once for it.
     *
     * A failure is caught here rather than left to the caller: nothing awaits a load, so a rejection
     * would surface on the customer's page and leave the SDK loading forever with its commands queued.
     * The SDK exits instead and the queued commands still run. They cannot have run already, because
     * every path runs them as its last step and running them never throws.
     */
    protected async _stopLoadAtConsentDenied(): Promise<void> {
        this._logger.info('Consent denied; the SDK will not start.')
        this._isLoadHeldForConsent = false

        try {
            this.userStore._haltTrackingForThisPage()

            const domainConfig =
                (await this._getStoredOrInjectedDomainConfig()) ??
                (await this._fetchDomainConfigForUserDeletionUrlTrigger())
            if (!domainConfig) {
                await this._handleExit('Consent denied.')
                await this._runQueuedCommands()
                return
            }

            this._applyLoadedDomainConfig(domainConfig)
            this.pushWorkerService = new PushWorkerService(this.loadConfig, domainConfig)

            // A deletion the URL requests, and a browser already halted, are handled exactly as on any
            // other load.
            if (await this._exitIfTrackingHalted()) {
                return
            }

            const subscription = await this._resolveSubscriptionForTeardown()
            if (subscription) {
                await this._teardownSubscriptionAndHaltTracking(
                    TrackedEventAction.OPT_OUT,
                    subscription,
                    OptOutSource.CONSENT_DENIED,
                )
            } else {
                await this._handleExit('Consent denied.')
            }
            await this._runQueuedCommands()
        } catch (err) {
            const error = ensureError(err)
            this._logger.error(error)
            this.eventManager._track(error)
            await this._handleExit('Consent denied.')
            await this._runQueuedCommands()
        }
    }

    /**
     * Fetches the domain config, through the same provider a started load uses, only when the URL
     * requests a deletion. Denied consent otherwise sends nothing, but a deletion the visitor asked
     * for outweighs that one request: without the domain the deletion cannot be sent or scoped.
     *
     * @returns the fetched config, or `null` when the URL requests no deletion or the fetch fails.
     */
    private async _fetchDomainConfigForUserDeletionUrlTrigger(): Promise<DomainConfig | null> {
        if (!this._isUserDeletionUrlTriggerPresent()) {
            return null
        }

        this.domainSettingsProvider = new DomainSettingsProvider(this.loadConfig.domainKey, this.settingsStore)
        this.domainConfigProvider = this.domainSettingsProvider._configProvider

        const domainConfigResult = await this.domainConfigProvider._get()
        if (domainConfigResult.isErr()) {
            this._logger.warn(`Unable to load the domain config for a deletion: ${domainConfigResult.getErr().message}`)
            return null
        }

        return domainConfigResult.unwrap()
    }

    /**
     * Applies consent the site reports at runtime, or before `load`. A load held while consent was
     * pending starts or stops here. A page that has already started is never restarted: a grant
     * there undoes a consent-made opt-out from the next load on, and a denial withdraws now.
     *
     * Never rejects: the `consent` command does not await it, so a rejection would surface on the
     * customer's page. A failure is logged and tracked instead.
     */
    public async _handleConsentUpdate(value: unknown): Promise<void> {
        try {
            await this._applyConsentUpdate(value)
        } catch (err) {
            const error = ensureError(err)
            this._logger.error(error)
            this.eventManager._track(error)
        }
    }

    private async _applyConsentUpdate(value: unknown): Promise<void> {
        const state = this._normalizeConsentStateOrWarn(value, 'the consent command', true)
        if (state === undefined) {
            return
        }

        if (!this.isLoading && !this._isLoaded) {
            this._recordConsentReportedBeforeLoad(state)
            return
        }

        if (this._isLoadHeldForConsent) {
            if (state === 'granted') {
                await this._releaseLoadHeldForConsent()
            } else if (state === 'denied') {
                await this._stopLoadAtConsentDenied()
            }
            return
        }

        if (state === 'pending') {
            this._logger.debug('Consent is already decided on this page; pending changes nothing.')
            return
        }

        if (state === 'denied') {
            // Halted now, not when the withdrawal below gets its turn: until then, startup still
            // running and the event queue would keep sending for a visitor who has just said no.
            this.userStore._haltTrackingForThisPage()
            this._isConsentDeniedOnThisPage = true
        }

        const applyConsentInTurn = () =>
            this._executeWhenConfigured(() =>
                this._runHaltMarkerChangeInTurn(() => this._applyConsentToStartedPage(state)),
            )

        if (this._isLoaded) {
            await applyConsentInTurn()
            return
        }

        // A load that is still running has passed the consent check. The change applies once it
        // finishes, or once a stopped load drains its queue, and is queued with opt-out, opt-in and
        // deletion so that all of them run in the order the site called them: a deletion called
        // before a denial must still carry the subscription and external id the denial removes.
        this._registerOnLoadedCallback(applyConsentInTurn)
    }

    /**
     * Keeps consent reported before `load` for the load to use, and applies the page halt it implies
     * straight away: the impression checked when the bundle boots, and the event queue's flush of
     * events an earlier page stored, would otherwise go out before the load reads consent.
     */
    private _recordConsentReportedBeforeLoad(state: PushSdkConsentState): void {
        const previousState = this._consentStateOverride
        this._consentStateOverride = state

        if (state !== 'granted') {
            this.userStore._haltTrackingForThisPage()
            return
        }

        if (previousState === 'pending' || previousState === 'denied') {
            this.userStore._resumeTrackingForThisPage()
            // The halt just lifted may have held back the impression checked at boot.
            this._sendImpressionEvent()
        }
    }

    /**
     * A repeated `load` is otherwise ignored, but consent it carries is the site's latest report and
     * is applied as the `consent` command would apply it.
     */
    private async _applyConsentFromRepeatedLoad(config: PushSdkConfig | undefined): Promise<void> {
        const state = this._normalizeConsentStateOrWarn(config?.consent, 'a repeated load')
        if (state === undefined) {
            return
        }

        this._logger.info(`Applying consent '${state}' from a repeated load.`)
        await this._handleConsentUpdate(state)
    }

    /**
     * Runs `change` once every halt marker change queued before it has finished. Opting out, opting
     * in, a deletion and consent applied to a started page each read the opt-out or deletion marker
     * and then write it. Run together, two could both find the browser not opted out and the last
     * write would win, so a consent denial could record an explicit opt-out as one that consent
     * caused, and a later grant would undo it.
     *
     * A change must never wait on this chain from inside another change, or neither would finish.
     * None of them runs queued commands; the load and its early stops, which do, are not run on it,
     * and while they run every command is still queued.
     */
    protected _runHaltMarkerChangeInTurn<T>(change: () => Promise<T>): Promise<T> {
        const changeResult = this._haltMarkerChangesInTurn.then(change)
        this._haltMarkerChangesInTurn = changeResult.catch(() => undefined)
        return changeResult
    }

    /**
     * Starts a load that was held while consent was pending. Nothing ran while it was held, so this
     * is its first start and every lifecycle callback fires once, in the usual order.
     */
    protected async _releaseLoadHeldForConsent(): Promise<void> {
        this._logger.info('Consent granted; starting the SDK.')
        this._isLoadHeldForConsent = false
        this._effectiveConsentState = 'granted'
        this.userStore._resumeTrackingForThisPage()
        // The impression is sent when the bundle boots, unless the hold was already in place then.
        // Sent again here, the released load reports its page view as any other load does; a page
        // that already reported one sends nothing more.
        this._sendImpressionEvent()

        await this._startLoadAfterConsentCheck(this._loadConfig)
    }

    /**
     * Applies a decided consent to a page whose load has already run. A grant cannot restart a page
     * that stopped, so it only clears an opt-out that denied consent made, for the next load to act
     * on. A denial withdraws a subscription as a consent opt-out, or only stops this page, already
     * halted when the denial was reported, when there is nothing to withdraw.
     */
    protected async _applyConsentToStartedPage(state: Exclude<PushSdkConsentState, 'pending'>): Promise<void> {
        if (state === 'granted') {
            if (await this._clearOptOutSetByConsent()) {
                this._logger.info('Consent granted; this takes effect on the next page load.')
            } else if ((await this.userStore._getIsOptedOut()) || (await this.userStore._getIsInDeletedState())) {
                // Only a marker the visitor set is worth a warning. A consent-made marker was just
                // cleared, and a halt for this page alone ends with the page.
                this._logger.warn('Consent granted, but the visitor opted out or asked to be deleted; nothing changes.')
            } else if (this._isExited || (await this.userStore._getIsTrackingHalted())) {
                // A page that stopped cannot be restarted, but nothing stored stands in the way of the
                // next load, so the integration only needs to know when the grant applies.
                this._logger.info('Consent granted; this takes effect on the next page load.')
            }
            return
        }

        try {
            await this._withdrawForConsentDeniedOnStartedPage()
        } catch (err) {
            // A load that a denial interrupted is not finished as a normal load, so without an exit
            // here the page would end neither ready nor exited.
            const error = ensureError(err)
            this._logger.error(error)
            this.eventManager._track(error)
            await this._handleExit('Consent denied.')
        }
    }

    /**
     * Withdraws a subscription as an opt-out consent caused, on a page whose load has run, including
     * one that exited for an unrelated reason: a subscription outlives an exit, and an explicit
     * opt-out on such a page withdraws it too. The page halt is not checked here, because the denial
     * set it before this ran.
     */
    private async _withdrawForConsentDeniedOnStartedPage(): Promise<void> {
        const [isOptedOut, isDeleted] = await Promise.all([
            this.userStore._getIsOptedOut(),
            this.userStore._getIsInDeletedState(),
        ])
        if (isOptedOut || isDeleted) {
            // The visitor's own opt-out or deletion already covers this browser, and must not be
            // recorded again as an opt-out consent caused, which a later grant would undo.
            await this._handleExit('Consent denied.')
            return
        }

        // A page that exited before its domain config loaded has no way yet to find a subscription.
        // A stored or injected config of any age has what the withdrawal needs, as on a denied load;
        // with none, this origin never loaded the SDK and holds nothing to withdraw.
        if (!this.pushWorkerService) {
            const domainConfig = await this._getStoredOrInjectedDomainConfig()
            if (domainConfig) {
                this._applyLoadedDomainConfig(domainConfig)
                this.pushWorkerService = new PushWorkerService(this.loadConfig, domainConfig)
            }
        }

        const subscription = await this._resolveSubscriptionForTeardown()
        if (subscription) {
            await this._teardownSubscriptionAndHaltTracking(
                TrackedEventAction.OPT_OUT,
                subscription,
                OptOutSource.CONSENT_DENIED,
            )
            return
        }

        // Nothing durable is written for a browser with nothing to withdraw, but events stored
        // before the denial are discarded as an opt-out discards them, so no later page sends them.
        await this.eventManager._discardQueuedEvents()
        await this._handleExit('Consent denied.')
    }

    protected async _handleExit(exitException?: PNException | string): Promise<void> {
        if (this._isExited) {
            this._logger.debug('SDK has already exited.')
            return
        }

        let exitMessage = `SDK Exited`
        if (exitException) {
            exitMessage += `: ${typeof exitException === 'string' ? exitException : exitException._compositeMessage}`
        }
        this._logger.warn(exitMessage)

        this._isLoaded = true
        this._isExited = true
        this._isEnabled = false
        this.promptsController?._disable()
        // An exited SDK shows nothing: pausing the timer alone would leave a prompt already on
        // screen able to subscribe the visitor.
        this.promptsController?._suppressPrompts()
        // Both would otherwise keep running for the rest of the page: the observer re-runs
        // reconciliation on any permission change, and the heartbeat restarts on activity.
        this.notificationPermissionService?._stopWatching()
        this._heartbeatManager?._stop()
        this.sessionManager._stop()
        this.pageViewProcessor?._stop()
        this.scrollDepthObserver?._stop()

        try {
            const [storedSubscriberStatus, isInDeletedState, isTrackingHalted] = await Promise.all([
                this.userStore._getSubscriberStatus(),
                this.userStore._getIsInDeletedState(),
                this.userStore._getIsTrackingHalted(),
            ])

            // The stored status stops being maintained once tracking is halted, so it cannot be
            // trusted to say whether the browser is subscribed.
            const subscriberStatus = isTrackingHalted ? SubscriberStatus.NOT_DETERMINED : storedSubscriberStatus
            this.onPushSDKDidExitWithSubscriberStatus!(subscriberStatus, isInDeletedState)
        } catch (err) {
            this._logger.error(ensureError(err))
        }
    }

    protected async _load() {
        await this._logInitializingState()

        const domainConfigReq = await this.domainConfigProvider._get()
        if (domainConfigReq.isErr()) {
            const err = domainConfigReq.getErr()
            await this._handleExit(
                err.code === PNExceptionCode.DOMAIN_CONFIGURATION_LOAD_FAILED
                    ? err
                    : new PNException(PNExceptionCode.DOMAIN_CONFIGURATION_LOAD_FAILED, domainConfigReq.getErr()),
            )
            // Queued commands still run for the same reason as on the other early exits below.
            await this._runQueuedCommands()
            return
        }

        const domainConfig = domainConfigReq.unwrap()
        this._applyLoadedDomainConfig(domainConfig)

        await this._handleDomainConfigInitialLoadCompleted(domainConfig)
    }

    /**
     * Applies what every later step reads from the domain config: cookie scope, event identity and
     * whether this host may write the storage it shares.
     */
    protected _applyLoadedDomainConfig(domainConfig: DomainConfig): void {
        CookieDataStore._setDomain(domainConfig)
        TrackedEventUtils._domainKey = this._domainKey
        // Withdrawals and deletions are delivered without the send-time lookups that would otherwise
        // fill the domain id, and the events endpoint drops an event without one. Set here, every
        // event built from now on carries it from the start.
        TrackedEventUtils._domainId = domainConfig.id ?? null

        // An event-only host reads the subscriber it shares with the rest of the domain and
        // caches it locally. It cannot subscribe, so it has nothing to contribute about who that
        // subscriber is and must not write over the host that does. This is the first point the
        // mode is known, and it has to be applied here: a getter that saves a default value on a
        // first read - the deleted-state check behind every event send is one - would otherwise
        // reach the shared cookie before the event-only path is entered.
        if (isEventOnlyMode(domainConfig)) {
            this.userStore._setSharedStorageReadOnly()
        }
    }

    protected async _logInitializingState() {
        this._logger.debug(`Initializing SDK v${Globals._libraryVersion} (${Globals._isProd ? 'P' : 'D'}-${this.iid})`)
        this._logger.info(`Loading key: ${this._domainKey}`)
    }

    /**
     * Determines whether any prompt in the active configuration uses the scroll depth visitor
     * trigger. Used to gate {@link ScrollDepthObserver} tracking: when no prompt uses scroll depth
     * the observer is initialized without subscribing to the shared scroll signal and performs no
     * computation.
     *
     * Reads prompts through the shared prompts view of {@link DomainSettingsProvider}, so this
     * check cannot cost a request the PromptsController has already paid for. Failures and empty
     * configs are treated as "no scroll depth usage" so tracking stays disabled.
     */
    protected async _activeConfigUsesScrollDepth(
        promptsProvider: RemoteResourceProvider<PromptGroup[]>,
    ): Promise<boolean> {
        try {
            const promptsRes = await promptsProvider._get()
            if (promptsRes.isErr()) {
                return false
            }

            return promptGroupsUseScrollDepthTrigger(promptsRes.unwrap())
        } catch (err) {
            this._logger.warn(ensureError(err))
            return false
        }
    }

    protected async _logUserInfo() {
        let piid = await this.userStore._getAnonymousId()
        if (piid) {
            this._logger.info(`Anonymous ID: ${piid}`)
        }

        let externalId = await this.userStore._getExternalId()
        if (externalId) {
            this._logger.info(`External ID: ${externalId}`)
        }
    }

    protected async _logCurrentSubscriptionStatus() {
        const [subStatus, expiration] = await Promise.all([
            this.userStore._getSubscriberStatus(),
            this.userStore._getSubscriberStatusDismissedExpiration(),
        ])

        let statusLog = `Current subscription status: ${subscriberStatusToSentenceString(subStatus)}`
        if (subStatus === SubscriberStatus.DISMISSED && expiration !== null) {
            statusLog += ` - expires in ${Math.round((expiration - new Date().getTime()) / 1000)}s`
        }

        this._logger.info(statusLog)
    }

    protected async _handleDomainConfigInitialLoadCompleted(domain: DomainConfig) {
        const hostValid = this._validateHostAvailability(domain)
        if (!hostValid) {
            console.warn(
                '%c[PushlySDK: WARNING]\n%cRefusing to run due to domain not present in allow list; This can be configured via the platform or by reaching out to your account manager.',
                'font-size:1.25em;font-weight:600;font-style:italic;color:#4DBCA2;',
                'font-size:1.25em;',
            )
            this.debugController._debugPromptNotShown(DebugEventReason.HOST_NOT_WHITELISTED)
            await this._handleExit(new PNException(PNExceptionCode.HOST_NOT_ALLOWED))
            return
        }

        // Legacy loaded event does not have a corresponding lifecycle callback
        await this._dispatchLegacyEventCallbacks(PushSdkEvent.LOADED)

        // Before the external id is stored and before the halt check, so a visitor whose consent
        // returned is identified and loaded normally on this same page.
        if (this._effectiveConsentState === 'granted' && (await this._clearOptOutSetByConsent())) {
            this._logger.info('Consent granted; the opt-out it caused is undone.')
            // The marker just cleared held back the impression checked when the bundle booted.
            this._sendImpressionEvent()
        }

        // Stored before the halt check so a deletion the URL requests reports the id given at load,
        // not one left over from an earlier visit. An opted-out browser keeps no identifier.
        if (this.loadConfig.externalId && !(await this.userStore._getIsOptedOut())) {
            await this.userStore._setExternalId(this.loadConfig.externalId)
        }

        const pushWorkerService = new PushWorkerService(this.loadConfig, domain)
        this.pushWorkerService = pushWorkerService

        if (await this._exitIfTrackingHalted()) {
            return
        }

        if (isEventOnlyMode(domain)) {
            this._logger.debug('[Event Only Mode] Skipping notification & prompt services')
            await this.userStore._synchronizeData()
        } else {
            this._logger.debug('Starting notification services')

            this.notificationPermissionService = new NotificationPermissionService(
                domain,
                pushWorkerService,
                this.eventManager,
                this.userStore,
                this,
            )

            // Single shared prompts view: reused by both the scroll-depth gating check and the
            // PromptsController so prompts are loaded once. Backed by the same document and the
            // same in-flight fetch as the domain config, so neither reader can cost a request the
            // other has already paid for.
            const promptsProvider = this.domainSettingsProvider._promptsProvider

            const usesScrollDepth = await this._activeConfigUsesScrollDepth(promptsProvider)
            this.scrollDepthObserver = ScrollDepthObserver._init(usesScrollDepth)

            if (usesScrollDepth) {
                // Baseline freezing is gated on recent user input (wheel/touch/key/mousedown) so
                // browser-initiated scrolls (restoration, SPA shrink-clamps, anchor jumps) cannot
                // establish a depth denominator. Only bound when a prompt actually uses the
                // trigger, so other domains pay for no extra listeners.
                this.scrollDepthObserver._bindGestureSignals()
            }

            this.promptsController = new PromptsController(
                this.loadConfig,
                domain,
                promptsProvider,
                this.notificationPermissionService,
                this.userStore,
                this.settingsStore,
                this.sessionManager,
                this.scrollDepthObserver,
                this.eventManager,
                this.debugController,
                this,
            )

            this._heartbeatManager = new HeartbeatManager(
                this.eventManager,
                this.userStore,
                this.sessionStore,
                domain.session,
                () => this.debugController._getIsEventsEnabled(),
            )

            // Drive the observer from the single shared passive scroll listener registered by
            // the heartbeat manager rather than registering a second listener. The Event is
            // forwarded so the observer can attribute the scroll to its container.
            this._heartbeatManager._subscribeToScroll((ev) => this.scrollDepthObserver?._onScroll(ev))

            let exitException: PNException | null = null

            const webPushSupport = await NotificationPermissionService._getWebPushSupport()
            if (webPushSupport.isErr()) {
                exitException = webPushSupport.getErr()

                this.onPushSDKDidNotDetectWebPushSupport(exitException)
            } else {
                this.onPushSDKDidDetectWebPushSupport()

                await this._logUserInfo()

                await this.userStore._synchronizeData()
                await this._logCurrentSubscriptionStatus()

                // Setup Global Event Parameters
                try {
                    await this.userStore
                        ._getAnonymousId()
                        .then((id) => {
                            TrackedEventUtils._anonymousId = id
                        })
                        .catch()
                } catch {}

                const notifPermissionSvcInit = await this.notificationPermissionService._init()
                if (notifPermissionSvcInit.isErr()) {
                    const notifPermissionSvcInitErr: Error | PNException = notifPermissionSvcInit.getErr()

                    if (
                        notifPermissionSvcInitErr instanceof PNException &&
                        notifPermissionSvcInitErr._isExitException
                    ) {
                        exitException = notifPermissionSvcInitErr

                        // sent regardless of init err code
                        if (notifPermissionSvcInitErr.code === PNExceptionCode.PUSH_WORKER_REGISTRATION_EXCEPTION) {
                            this.debugController._debugPromptNotShown(DebugEventReason.SW_INSTALL_FAILED)
                        }
                    } else {
                        this._logger.warn(notifPermissionSvcInitErr.message)
                    }
                }
            }

            this.promptsController._setIsWebPushSupported(webPushSupport)

            if (exitException) {
                await this._handleExit(exitException)
                // A withdrawal or deletion queued before load must still be recorded. Otherwise a
                // later load that gets further would keep, or re-create, the subscription of a
                // visitor who asked for it to go.
                await this._runQueuedCommands()
                return
            }

            // Kickoff permissions evaluation and sw installation, if needed
            await this.notificationPermissionService._synchronize()

            // Watch for out-of-band permission changes (e.g. browser site-setting toggles) so a
            // revoke/grant is reconciled live instead of waiting for the next page load. The
            // callback re-evaluates page-view state so an in-session subscribe still emits a view.
            this.notificationPermissionService._watch(() => this.pageViewProcessor?._evaluate())
        }

        // setup page view processing timer
        this.pageViewProcessor = PageViewProcessor._init(
            this.eventManager,
            this.userStore,
            this.sessionManager.transient.store,
            {
                onPageChanged: async () => {
                    // Reset Current_Page_Max_Depth so each page (including SPA route changes)
                    // starts fresh.
                    this.scrollDepthObserver?._onPageChanged()
                    await pushWorkerService._getRegistration()
                },
            },
        )

        if (isForceDebugLogsFlagEnabled(domain)) {
            this._logger.debug('Forcing DEBUG logs')
            this._logger.logLevel = LogLevel.VERBOSE
        }

        this._evaluateUserLocaleAndTimeZoneSettings().then()

        await this._setIsLoaded(domain)
    }

    protected async _evaluateUserLocaleAndTimeZoneSettings() {
        try {
            const [currLocale, nextLocale, currTimeZone, nextTimeZone] = await Promise.all([
                this.userStore._getLocale(),
                Intl.DateTimeFormat().resolvedOptions().locale,
                this.userStore._getTimeZone(),
                Intl.DateTimeFormat().resolvedOptions().timeZone,
            ])

            const txs: Promise<any>[] = []

            if (!currLocale || (nextLocale && currLocale !== nextLocale)) {
                txs.push(
                    this.userStore
                        ._setLocale(nextLocale)
                        .then(() => TrackedEventUtils._getProfileLanguageEvent(nextLocale))
                        .then((event) => this.eventManager._track(event)),
                )
            }
            if (!currTimeZone || (nextTimeZone && currTimeZone !== nextTimeZone)) {
                txs.push(
                    this.userStore
                        ._setTimeZone(nextTimeZone)
                        .then(() => TrackedEventUtils._getProfileTimeZoneEvent(nextTimeZone))
                        .then((event) => this.eventManager._track(event)),
                )
            }

            await Promise.all(txs).catch((err) => this._logger.error(err))
        } catch (err) {
            const error = ensureError(err)
            this._logger.error(error)
        }
    }

    public async _getUserDetails(): Promise<UserDebugDetails> {
        const userId = await this.userStore._getAnonymousId()
        const subscriberStatus = await this.userStore._getSubscriberStatus()
        const externalId = await this.userStore._getExternalId()
        const browserPermissions = await this._getBrowserPermissions()

        return {
            userId,
            subscriberStatus,
            externalId,
            browserPermission: browserPermissions?.permission ?? null,
            endpoint: browserPermissions?.token?.endpoint ?? null,
        }
    }

    protected async _getBrowserPermissions(): Promise<ProviderPermissions | null> {
        if (!this.notificationPermissionService) {
            return null
        }

        return await this.notificationPermissionService._getCurrentPermissions()
    }

    public async _toggleDebugPanel(display: boolean = true): Promise<void> {
        try {
            return display
                ? this.debugController._showDebugPanel(await this._getUserDetails())
                : this.debugController._hideDebugPanel()
        } catch (err) {
            this.eventManager._track(ensureError(err))
        }
    }

    public _handleUserDeletionRequest(): Promise<void> {
        return this._runHaltMarkerChangeInTurn(() => this._requestUserDeletion())
    }

    private async _requestUserDeletion(): Promise<void> {
        if (await this.userStore._getIsInDeletedState()) {
            this._logger.warn('User has already requested to be deleted.')
            return
        }

        this._logger.verbose('Requesting user deletion')
        await this._teardownSubscriptionAndHaltTracking(TrackedEventAction.USER_DELETION_REQUEST)
    }

    /**
     * Stops the load for a browser whose visitor opted out or asked to be deleted, and acts on a
     * deletion requested through the URL. Runs before anything reconciles permissions: a browser
     * that is still granted but has no subscription would otherwise be re-subscribed.
     *
     * @returns whether the load was stopped.
     */
    protected async _exitIfTrackingHalted(): Promise<boolean> {
        const [isOptedOut, isDeleted, optOutSource] = await Promise.all([
            this.userStore._getIsOptedOut(),
            this.userStore._getIsInDeletedState(),
            this.userStore._getOptedOutSource(),
        ])

        // Deletion is the stronger request, so a deletion URL runs on an opted-out browser as well.
        if (!isDeleted && this._isUserDeletionUrlTriggerPresent()) {
            await this._teardownSubscriptionAndHaltTracking(TrackedEventAction.USER_DELETION_REQUEST)
            await this._runQueuedCommands()
            return true
        }

        if (!isOptedOut && !isDeleted) {
            return false
        }

        // A marker written before the domain was known can sit only in this origin's storage, and a
        // halted load never syncs, so each one set is written again to reach the cookie sibling
        // hosts share. A rewrite keeps the source it found, or consent granted later could no longer
        // undo an opt-out that consent caused.
        if (isOptedOut) {
            await this.userStore._setIsOptedOut(true, optOutSource ?? OptOutSource.EXPLICIT_REQUEST)
        }
        if (isDeleted) {
            await this.userStore._setIsInDeletedState(true)
        }

        // A subscription can outlive the marker: an unsubscribe that failed, or a sibling host that
        // learned of the halt only through the shared cookie and never reported its own.
        const subscription = await this._resolveSubscriptionForTeardown()
        if (subscription && isOptedOut) {
            // The opt-out is reported again so the server learns of this host's subscription. Its
            // new event id is harmless: the server only sets state from an opt-out.
            await this._teardownSubscriptionAndHaltTracking(
                TrackedEventAction.OPT_OUT,
                subscription,
                optOutSource ?? OptOutSource.EXPLICIT_REQUEST,
            )
        } else if (subscription) {
            // A deletion is not reported again: each report is a separate request to erase the
            // visitor, and the one already made covers every subscription.
            await this._removeSubscriptionWithoutReport(subscription)
        } else {
            await this._handleExit(isOptedOut ? 'User is opted out.' : 'User is deleted.')
        }

        // Storage is per origin, so a sibling host keeps its own copy of the external id and its
        // own stored event queue until a halted load there removes them. Done after any teardown,
        // whose report still carries the id. The queue goes for a deletion as well as a withdrawal,
        // so a later opt-in cannot release events collected before the halt under an id the
        // deletion replaced. The external id goes only for a withdrawal; a deletion leaves it.
        if (isOptedOut) {
            await this.userStore._removeExternalId()
        }
        if (isOptedOut || isDeleted) {
            await this.eventManager._discardQueuedEvents()
        }

        await this._runQueuedCommands()
        return true
    }

    /**
     * Removes a subscription left behind on a deleted browser without reporting it: unsubscribes,
     * closes displayed notifications and exits.
     */
    protected async _removeSubscriptionWithoutReport(subscription: PushSubscription): Promise<void> {
        if (this.pushWorkerService) {
            await this.pushWorkerService._unsubscribe(subscription)
            await this.pushWorkerService._closeDisplayedNotifications()
        }

        await this.userStore._setSubscriberStatus(SubscriberStatus.NOT_DETERMINED)
        await this._handleExit('User is deleted.')
    }

    protected _isUserDeletionUrlTriggerPresent(): boolean {
        if (this.loadConfig.disableUserDeletionUrlTrigger === true) {
            return false
        }

        return this._currentPageSearchParams.has(Globals._udrUrlSearchParameter)
    }

    /**
     * Runs commands the integration issued before load, for a load that stops early. They still
     * run so that a withdrawal or deletion queued in the same breath is honoured; anything they
     * track is held back by the halt.
     */
    protected async _runQueuedCommands(): Promise<void> {
        this.isLoading = false
        await this._executePostConfigActions().catch((err) => this._logger.error(err))
        await this._runOnLoadedCallbacksInOrder()
    }

    /**
     * One after another, in the order they were registered: an external id given before an
     * opt-out or a deletion has to be stored before that request reads it.
     */
    protected async _runOnLoadedCallbacksInOrder(): Promise<void> {
        for (const callback of this._onLoadedCallbacks ?? []) {
            // A callback that throws synchronously is caught too, so one failing command cannot stop
            // the rest from running.
            try {
                await callback()
            } catch (err) {
                this._logger.error(err)
            }
        }
    }

    /**
     * Withdraws this browser's consent: reports `opt_out`, removes the push subscription and halts
     * all tracking. It applies to this browser only and lasts until the visitor opts back in or clears
     * the site's data. It is not erasure, which goes through the Privacy API.
     */
    public _handleOptOutRequest(): Promise<void> {
        return this._runHaltMarkerChangeInTurn(() => this._optOutExplicitly())
    }

    private async _optOutExplicitly(): Promise<void> {
        if (await this.userStore._getIsOptedOut()) {
            // The withdrawal is already on record with the server. What changes is that a visitor who
            // asked for it themselves must stay opted out even when the site later reports consent.
            if ((await this.userStore._getOptedOutSource()) === OptOutSource.CONSENT_DENIED) {
                await this.userStore._setIsOptedOut(true, OptOutSource.EXPLICIT_REQUEST)
                this._logger.info('User opted out; consent granted later will not undo it.')
                return
            }

            this._logger.warn('User has already opted out.')
            return
        }

        this._logger.verbose('Opting user out')
        await this._teardownSubscriptionAndHaltTracking(TrackedEventAction.OPT_OUT)
    }

    /**
     * Lets a visitor who opted out or asked to be deleted in this browser be subscribed and tracked
     * again. It takes effect on the next page load: a halted page has already shut the SDK down.
     *
     * After a deletion the browser returns as a new visitor. The anonymous id was replaced when the
     * deletion ran, so nothing reported from now on joins the erased profile, and a deletion request
     * still waiting to be delivered is kept.
     */
    public _handleOptInRequest(): Promise<void> {
        return this._runHaltMarkerChangeInTurn(() => this._optInAgain())
    }

    private async _optInAgain(): Promise<void> {
        const [isDeleted, isOptedOut] = await Promise.all([
            this.userStore._getIsInDeletedState(),
            this.userStore._getIsOptedOut(),
        ])

        if (!isDeleted && !isOptedOut) {
            this._logger.debug('User is not opted out or deleted.')
            return
        }

        if (isDeleted && !(await this.userStore._setIsInDeletedState(false))) {
            this._logger.warn('Unable to store the opt-in; this browser stays deleted.')
            return
        }

        if (isOptedOut) {
            if (!(await this.userStore._setIsOptedOut(false))) {
                this._logger.warn('Unable to store the opt-in; this browser stays opted out.')
                return
            }

            await this._removePendingOptOutWriteAheadEvents()
        }

        this._logger.info('User opted in; this takes effect on the next page load.')
    }

    /**
     * The visitor's latest choice is to be subscribed, so an older withdrawal still waiting to be
     * delivered must not arrive afterwards and undo it. A pending deletion request stands.
     */
    protected async _removePendingOptOutWriteAheadEvents(): Promise<void> {
        const pendingWriteAheadEvents = await this.settingsStore._getWriteAheadEvents()
        for (const pendingEvent of pendingWriteAheadEvents) {
            if (pendingEvent.action === TrackedEventAction.OPT_OUT) {
                await this.settingsStore._removeWriteAheadEvent(pendingEvent.event_id)
            }
        }
    }

    /**
     * Clears an opt-out that consent caused, now that the site reports consent granted. An explicit
     * opt-out and a deletion are left alone. Returns whether one was cleared.
     */
    protected async _clearOptOutSetByConsent(): Promise<boolean> {
        if ((await this.userStore._getOptedOutSource()) !== OptOutSource.CONSENT_DENIED) {
            return false
        }

        if (!(await this.userStore._setIsOptedOut(false))) {
            this._logger.warn('Unable to store the consent grant; this browser stays opted out.')
            return false
        }

        await this._removePendingOptOutWriteAheadEvents()
        return true
    }

    /**
     * Reports `action`, then halts this browser until the visitor opts back in or clears the site's
     * data (opting in clears either marker; a deletion has already replaced the anonymous id): no push subscription, no tracking, no prompts.
     * Shared by opt-out and deletion so the two cannot drift apart in what they stop.
     *
     * `resolvedSubscription` is for a caller that has already found the subscription. Otherwise it
     * is resolved here, waiting on a still-activating worker, because a cold read can report none
     * while one exists, and the unsubscribe would then silently do nothing.
     *
     * `optOutSource` records why an opt-out was made, so that consent granted again undoes only an
     * opt-out that consent caused.
     */
    protected async _teardownSubscriptionAndHaltTracking(
        action: TrackedEventAction.OPT_OUT | TrackedEventAction.USER_DELETION_REQUEST,
        resolvedSubscription?: PushSubscription | null,
        optOutSource: OptOutSource = OptOutSource.EXPLICIT_REQUEST,
    ): Promise<void> {
        const subscription =
            resolvedSubscription !== undefined ? resolvedSubscription : await this._resolveSubscriptionForTeardown()

        // The server finds the subscriber by this token, so it has to be the subscription as it was
        // before the unsubscribe below.
        const token = subscription?.toJSON() ?? TrackedEventUtils._token ?? null

        let externalId: string | null = null
        try {
            externalId = await this.userStore._getExternalId()
        } catch (err) {
            this._logger.warn(ensureError(err).message)
        }

        const event = await TrackedEventUtils._build(
            action,
            externalId ? { [TrackedEventAction.EXTERNAL_ID]: externalId } : {},
        )
        event.token = token

        // A command queued before a load whose domain config failed runs with no domain in memory.
        // The event is delivered without send-time lookups, and the events endpoint drops one that
        // names no domain while still answering 2xx, so it has to name the domain from the start.
        if (!event.domain_id) {
            event.domain_id = await this._getCachedDomainId()
        }
        if (!event.domain_key) {
            event.domain_key = this._domainKey ?? null
        }

        // A stored event is retried as it was stored, with no identity lookups at send time, so it
        // has to carry the anonymous id from the start. A load that stops before tracking starts,
        // or runs in event-only mode, has not put it in memory yet.
        if (!event.pushly_unique_user_id) {
            try {
                event.pushly_unique_user_id = await this.userStore._getAnonymousId()
            } catch (err) {
                this._logger.warn(ensureError(err).message)
            }
        }

        if (!(await this.eventManager._trackWriteAhead(event))) {
            this._logger.warn(`Unable to store ${action}; it is being sent once and will not be retried`)
        }

        // The marker goes in before the unsubscribe. A page closed between the two leaves the
        // marker for the next load to finish the job. The other order would leave a browser that
        // is still granted with no subscription, which the next load silently re-subscribes.
        const isMarkerStored =
            action === TrackedEventAction.OPT_OUT
                ? await this.userStore._setIsOptedOut(true, optOutSource)
                : await this.userStore._setIsInDeletedState(true)
        if (!isMarkerStored) {
            this._logger.warn(`The ${action} halt marker could not be stored; the next load may not stay halted`)
        }

        if (action === TrackedEventAction.USER_DELETION_REQUEST) {
            // The deletion request above carries the old id, and the server erases what was filed
            // under it. Anything this browser reports afterwards, including after an opt-in, must
            // start a new profile rather than join the erased one.
            const rotatedAnonymousId = await this.userStore._rotateAnonymousId()
            if (rotatedAnonymousId) {
                TrackedEventUtils._anonymousId = rotatedAnonymousId
            } else {
                this._logger.warn('The anonymous id could not be replaced; the next load may report under the old one')
            }
        }

        if (action === TrackedEventAction.OPT_OUT) {
            // An opted-out browser keeps no customer identifier on the device; the event above
            // already carries it to the server. Done after the marker, so the window between storing
            // the event and halting stays a single write.
            await this.userStore._removeExternalId()
        }

        // Nothing collected before the halt may be sent once the visitor opts back in. That holds
        // for a deletion as well as a withdrawal: the queued events carry the anonymous id the
        // deletion replaced, and sending them would re-link the browser to the erased profile.
        await this.eventManager._discardQueuedEvents()

        if (this.pushWorkerService) {
            await this.pushWorkerService._unsubscribe(subscription)
            await this.pushWorkerService._closeDisplayedNotifications()
        }

        await this.userStore._setSubscriberStatus(SubscriberStatus.NOT_DETERMINED)
        TrackedEventUtils._token = null

        await this._handleExit(action === TrackedEventAction.OPT_OUT ? 'User opted out.' : 'User requested deletion.')
    }

    /**
     * The domain id from an injected or previously cached domain config, or `null` on a first visit
     * whose config never loaded.
     */
    protected async _getCachedDomainId(): Promise<number | null> {
        if (Globals._injectedDomainConfig?.id) {
            return Globals._injectedDomainConfig.id
        }

        // An injected config without an id still falls through to the stored one.
        return (await this._getStoredDomainConfig())?.id ?? null
    }

    /**
     * The injected domain config, or the one stored by an earlier load whatever its age, or `null`
     * when this origin has never loaded one. Never fetches.
     */
    protected async _getStoredOrInjectedDomainConfig(): Promise<DomainConfig | null> {
        return Globals._injectedDomainConfig ?? (await this._getStoredDomainConfig())
    }

    /** The domain config stored by an earlier load whatever its age, or `null`. Never fetches. */
    private async _getStoredDomainConfig(): Promise<DomainConfig | null> {
        try {
            return (await this.settingsStore._getDomainConfig())?.value ?? null
        } catch (err) {
            this._logger.warn(ensureError(err).message)
            return null
        }
    }

    /**
     * The live subscription, waiting on a still-activating worker, or `null` when there is none or
     * it cannot be read. A teardown that cannot read the subscription still has to halt.
     */
    protected async _resolveSubscriptionForTeardown(): Promise<PushSubscription | null> {
        try {
            return (await this.pushWorkerService?._resolveColdSubscription()) ?? null
        } catch (err) {
            this._logger.warn(ensureError(err).message)
            return null
        }
    }

    protected _validateHostAvailability(domain: DomainConfig): boolean {
        let hostAvailable = false
        let allowedDomains = domain.whitelist_domains

        if (!allowedDomains) {
            hostAvailable = true
        } else {
            if (!Array.isArray(allowedDomains) || allowedDomains.length === 0) {
                hostAvailable = true
            } else {
                const loc = this._currentLocation
                const localhosts = ['localhost', '127.0.0.1']

                // Shares the matcher with `isEventOnlyMode` rather than testing raw entries.
                // Entry syntax carries meaning a plain pattern test does not reproduce - a leading
                // `*.` matches the apex, a trailing `*` must not cross into another registrable
                // domain, and a top-level `|` is alternation - and this is the gate that decides
                // whether the SDK runs on a host at all, so the two must not drift apart.
                hostAvailable = [...allowedDomains, ...localhosts].some((entry) => matchesDomainEntry(entry, loc))
            }
        }

        return hostAvailable
    }

    protected async _setIsLoaded(domainConfig: DomainConfig): Promise<void> {
        if (this._isLoaded) {
            return
        }

        this.isLoading = false
        this._isLoaded = true

        this._executePostConfigActions().catch((err) => this._logger.error(err))

        if (this.debugController._shouldShowDebugPanel()) {
            await this.debugController._showDebugPanel(await this._getUserDetails())
        }

        await this._runOnLoadedCallbacksInOrder()

        // A command above can opt the visitor out or delete them, and nothing below may start then.
        // A consent denial reported during the load is still being applied, and ends the page with
        // an exit of its own; a page the visitor refused must not finish loading meanwhile.
        if (this._isExited || this._isConsentDeniedOnThisPage) {
            return
        }

        this._logger.debug('SDK load completed')
        if (!this._isEnabled) {
            this._logger.debug('SDK has been disabled')
        }

        const subscriberState = await this.userStore._getSubscriberStatus()

        if (!this.pageViewProcessor) {
            this._logger.warn('Page View timer not available')
        } else {
            this.pageViewProcessor._start()
        }

        // Start scroll-depth tracking alongside the page-view processor, after config load. When
        // scroll depth tracking is disabled this is a no-op.
        this.scrollDepthObserver?._start()

        // Prompt display evaluations should occur after the pageViewProcessor
        // is started to prevent issues with stale session-time-on-page data.
        this.promptsController?._startDisplayEvaluations()

        this.onPushSDKDidFinishLoading!(domainConfig, subscriberState)
    }

    protected async _executePostConfigActions(): Promise<void> {
        const postConfigActions = [...(this._postConfigActionsQueue ?? [])]
        this._postConfigActionsQueue = []

        if (postConfigActions.length) {
            return Promise.all(
                postConfigActions.map((cb) => Promise.resolve(cb()).catch((err) => this._logger.error(err))),
            ).then()
        }
    }

    protected async _dispatchLegacyEventCallbacks(event: PushSdkEvent, ...data: any[]) {
        const callbacks = Object.values(this.legacyEventCallbacks[event] ?? {})
        if (!callbacks.length) {
            this._logger.verbose(`No on_${event} handlers found`)
            return
        }

        await Promise.all(
            callbacks.map((callback) =>
                Promise.resolve(callback(...data)).catch((err) => {
                    this._logger.error(`Error encountered in on_${event} handler:`, ensureError(err).message)
                }),
            ),
        )

        this._logger.verbose(`Completed ${callbacks.length} on_${event} handler(s)`)
    }

    /**
     * Sends the page's impression unless tracking is halted. Safe to call more than once: a load
     * released from a consent hold calls it again, and a page counts one impression however many
     * times it is asked.
     */
    protected _sendImpressionEvent() {
        const sendImpression = async () => {
            const isTrackingHalted = await this.userStore._getIsTrackingHalted()
            if (!isTrackingHalted && !this._isImpressionSentForThisPage) {
                this._isImpressionSentForThisPage = true
                this.debugController._debug(TrackedEventAction.SDK_IMPRESSION)
            }
        }

        sendImpression().then()
    }

    // region PushSDKLifecycleCallbacks Methods

    public onPushSDKDidDetectWebPushSupport(): void {
        this._pushSdkLifecycleCallbacks?.onPushSDKDidDetectWebPushSupport?.()
        this._dispatchLegacyEventCallbacks(PushSdkEvent.WEB_PUSH_SUPPORTED)
    }

    public onPushSDKDidNotDetectWebPushSupport(exception?: PNException): void {
        this._pushSdkLifecycleCallbacks?.onPushSDKDidNotDetectWebPushSupport?.()
        this._dispatchLegacyEventCallbacks(PushSdkEvent.WEB_PUSH_UNSUPPORTED, exception?.message, this.loadConfig, Env)
    }

    public onPushSDKDidFinishLoading(configuration: DomainConfig, subscriberStatus: SubscriberStatus): void {
        this._pushSdkLifecycleCallbacks?.onPushSDKDidFinishLoading?.(configuration, subscriberStatus)
        this._dispatchLegacyEventCallbacks(PushSdkEvent.READY, this._legacyContext)
    }

    public onPushSDKDidExitWithSubscriberStatus(
        status: SubscriberStatus,
        deleted: boolean,
        _exception?: PNException,
    ): void {
        this._pushSdkLifecycleCallbacks?.onPushSDKDidExitWithSubscriberStatus?.(status, deleted)
    }

    // endregion
    // region PromptLifecycleCallbacks Methods

    public async onPushSDKUserIsEligibleToPrompt() {
        this.promptLifecycleCallbacks?.onPushSDKUserIsEligibleToPrompt?.()
        this._dispatchLegacyEventCallbacks(PushSdkEvent.PROMPT_ELIGIBLE, {})
    }

    public async onPushSDKUserIsIneligibleToPrompt() {
        this.promptLifecycleCallbacks?.onPushSDKUserIsIneligibleToPrompt?.()
        this._dispatchLegacyEventCallbacks(PushSdkEvent.PROMPT_INELIGIBLE, {})
    }

    public async onPushSDKDidShowPrompt(id: number) {
        this.promptLifecycleCallbacks?.onPushSDKDidShowPrompt?.(id)

        const legacyPayload = await this._getLegacyPromptEventPayload(id)
        this._dispatchLegacyEventCallbacks(PushSdkEvent.PROMPT_SHOWN, legacyPayload)
    }

    public async onPushSDKFailedToShowPrompt(id: number | null, reason: PromptFailedToShowReason) {
        this.promptLifecycleCallbacks?.onPushSDKFailedToShowPrompt?.(id, reason)
    }

    public async onPushSDKDidReceivePromptResponse(id: number, response: boolean) {
        this.promptLifecycleCallbacks?.onPushSDKDidReceivePromptResponse?.(id, response)

        const legacyPayload = await this._getLegacyPromptEventPayload(id)
        if (response) {
            this._dispatchLegacyEventCallbacks(PushSdkEvent.PROMPT_ALLOWED, legacyPayload)
        } else {
            this._dispatchLegacyEventCallbacks(PushSdkEvent.PROMPT_DISMISSED, legacyPayload)
        }
    }

    public async _getLegacyPromptEventPayload(id: number): Promise<PushSdkPromptEventPayload> {
        return {
            prompt: { id },
            subscribed: await this.userStore._getIsSubscribed(),
        }
    }

    // endregion
    // region PermissionLifecycleCallbacks Methods

    public async onPushSDKDidRequestNotificationPermissions(): Promise<void> {
        this._permissionLifecycleCallbacks?.onPushSDKDidRequestNotificationPermissions?.()

        this._dispatchLegacyEventCallbacks(PushSdkEvent.PERMISSION_SHOWN, {})
        this._dispatchLegacyEventCallbacks(PushSdkEvent.LEGACY_PERMISSION_SHOWN, {})
    }

    public async onPushSDKDidReceivePermissionResponse(response: PermissionResponse): Promise<void> {
        this._permissionLifecycleCallbacks?.onPushSDKDidReceivePermissionResponse?.(response)

        switch (response) {
            case PermissionResponse.GRANTED:
                this._dispatchLegacyEventCallbacks(PushSdkEvent.PERMISSION_ALLOWED, {})
                this._dispatchLegacyEventCallbacks(PushSdkEvent.LEGACY_PERMISSION_ALLOWED, {})
                break
            case PermissionResponse.DENIED:
                this._dispatchLegacyEventCallbacks(PushSdkEvent.PERMISSION_DENIED, {})
                break
            case PermissionResponse.DISMISSED:
                this._dispatchLegacyEventCallbacks(PushSdkEvent.PERMISSION_DISMISSED, {})
                break
        }
    }

    public async onPushSDKDidReceivePermissionResponseWithError(
        response: PermissionResponse,
        error: Error,
    ): Promise<void> {
        this._permissionLifecycleCallbacks?.onPushSDKDidReceivePermissionResponseWithError?.(response, error)
    }

    public async onPushSDKDidRegisterForRemoteNotificationsWithDeviceToken(token: PushSubscriptionJSON): Promise<void> {
        this._permissionLifecycleCallbacks?.onPushSDKDidRegisterForRemoteNotificationsWithDeviceToken?.(token)
    }

    public async onPushSDKDidFailToRegisterForRemoteNotificationsWithError(error: Error): Promise<void> {
        this._permissionLifecycleCallbacks?.onPushSDKDidFailToRegisterForRemoteNotificationsWithError?.(error)
    }

    public async onPushSDKDidReceivePermissionStatusChange(permission: PermissionResponse): Promise<void> {
        this._heartbeatManager?.onSubscriptionChanged(permission == PermissionResponse.GRANTED)
        this._permissionLifecycleCallbacks?.onPushSDKDidReceivePermissionStatusChange?.(permission)

        switch (permission) {
            case PermissionResponse.GRANTED:
            case PermissionResponse.DENIED:
                this.promptsController?._disable()
                break

            case PermissionResponse.DISMISSED:
                this.promptsController?._enable()
                break
        }
    }

    // endregion
}
